import { ThemeEvents, CartAddEvent } from './events.js';

console.log('[cart-recos] loaded');

class CartDrawerCollectionRecommendations {
  constructor() {
    this.rootSelector = '[data-cart-recommendations]';
    this.trackSelector = '[data-cart-recommendations-track]';

    this.limit = 12;        // hoeveel we ophalen uit de collectie
    this.maxRender = 10;    // hoeveel we tonen
    this.cacheTtlMs = 10 * 60 * 1000; // 10 min cache

    this.init();
  }

  init() {
    this.renderAll();

    // Re-render wanneer de cart wijzigt
    document.addEventListener(ThemeEvents.cartUpdate, () => this.renderAll());

    // Ook als de drawer opnieuw opent (veilig)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.renderAll();
    });
  }

  async renderAll() {
    const containers = Array.from(document.querySelectorAll(this.rootSelector));
    const inDrawer = !!document.querySelector('.cart-drawer');

    console.log('[cart-recos] renderAll', { containers: containers.length, inDrawer });

    if (!containers.length) return;
    if (!inDrawer) return;

    const cart = await this.fetchCart();

    console.log('[cart-recos] cart', {
      hasCart: !!cart,
      items: cart?.items?.length,
      product_ids: cart?.items?.map((i) => i.product_id),
      ThemeRoutes: Theme?.routes,
    });

    if (!cart?.items?.length) {
      containers.forEach((el) => (el.hidden = true));
      return;
    }

    const cartProductIds = new Set(cart.items.map((i) => String(i.product_id)));

    for (const container of containers) {
      const handle = (container.dataset.collectionHandle || '').trim();
      const track = container.querySelector(this.trackSelector);

      console.log('[cart-recos] container', {
        handle,
        hasTrack: !!track,
        containerHidden: container.hidden,
      });

      if (!handle || !track) {
        container.hidden = true;
        continue;
      }

      const products = await this.fetchCollectionProducts(handle);

      console.log('[cart-recos] collection fetch', handle, {
        products: products?.length,
        sample: products?.[0],
      });

      const filtered = (products || [])
        .filter((p) => !cartProductIds.has(String(p.id)))
        .map((p) => this.normalizeProduct(p))
        .filter((p) => !!p.firstAvailableVariantId)
        .slice(0, this.maxRender);

      console.log('[cart-recos] filtered result', { filtered: filtered.length });

      if (!filtered.length) {
        container.hidden = true;
        continue;
      }

      track.innerHTML = filtered.map((p) => this.cardHTML(p)).join('');
      container.hidden = false;

      /* =====================================================
         Desktop UX: mouse wheel scrolls horizontally
         ===================================================== */
      if (track.dataset.wheelBound !== '1') {
        track.dataset.wheelBound = '1';

        track.addEventListener(
          'wheel',
          (e) => {
            const absX = Math.abs(e.deltaX || 0);
            const absY = Math.abs(e.deltaY || 0);

            // Only convert vertical wheel → horizontal scroll
            if (absY <= absX) return;

            track.scrollLeft += e.deltaY;
            e.preventDefault();
          },
          { passive: false }
        );
      }

      // Bind quick-add
      track.querySelectorAll('[data-reco-add]').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          const variantId = btn.getAttribute('data-variant-id');
          if (!variantId) return;

          btn.setAttribute('aria-busy', 'true');
          btn.disabled = true;

          try {
            await this.addToCart(variantId);
          } finally {
            btn.removeAttribute('aria-busy');
            btn.disabled = false;
          }
        });
      });
    }
  }

  async fetchCart() {
    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  async fetchCollectionProducts(handle) {
    const cacheKey = `cart_recos_collection_${handle}`;
    const cached = this.readCache(cacheKey);
    if (cached) return cached;

    const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${this.limit}&sort_by=best-selling`;

    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      const products = data?.products || [];

      this.writeCache(cacheKey, products);
      return products;
    } catch {
      return [];
    }
  }

  normalizeProduct(p) {
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const firstAvailable = variants.find((v) => v && v.available) || null;

    const firstImg =
      (Array.isArray(p.images) && p.images.length ? p.images[0] : null) ||
      p.image ||
      null;

    const image =
      typeof firstImg === 'string'
        ? firstImg
        : (firstImg && (firstImg.src || firstImg.url)) || null;

    const priceCents = firstAvailable ? this.toCents(firstAvailable.price) : null;
    const compareCents =
      firstAvailable && firstAvailable.compare_at_price
        ? this.toCents(firstAvailable.compare_at_price)
        : null;

    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      image,
      firstAvailableVariantId: firstAvailable ? String(firstAvailable.id) : null,
      priceCents,
      compareCents,
    };
  }

  cardHTML(p) {
    const img = p.image
      ? `<img class="cart-reco__img" src="${this.imgUrl(p.image, 180)}" alt="${this.escape(p.title)}" loading="lazy">`
      : '';

    const price = p.priceCents != null ? this.money(p.priceCents) : '';
    const compare =
      p.compareCents != null && p.priceCents != null && p.compareCents > p.priceCents
        ? `<span class="cart-reco__compare">${this.money(p.compareCents)}</span>`
        : '';

    return `
      <a class="cart-reco__card" href="/products/${p.handle}">
        <div class="cart-reco__media">${img}</div>
        <div class="cart-reco__meta">
          <div class="cart-reco__title">${this.escape(p.title)}</div>
          <div class="cart-reco__price">
            ${compare}
            <span class="cart-reco__now">${price}</span>
          </div>
        </div>

        ${
          p.firstAvailableVariantId
            ? `<button
                 class="cart-reco__add"
                 type="button"
                 data-reco-add
                 data-variant-id="${p.firstAvailableVariantId}"
                 aria-label="Toevoegen"
               >
                 <span class="svg-wrapper" aria-hidden="true">${this.plusIcon()}</span>
               </button>`
            : ''
        }
      </a>
    `;
  }

  async addToCart(variantId) {
    const sectionIds = Array.from(document.querySelectorAll('.cart-drawer [data-section-id]'))
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });

    const json = await res.json();

    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        didError: !!json?.status,
        source: 'cart-recommendations',
        itemCount: 1,
        variantId: String(variantId),
        sections: json?.sections,
      })
    );
  }

  money(cents) {
    const lang = document.documentElement.lang || 'nl';
    const locale = lang.includes('-') ? lang : `${lang}-${lang.toUpperCase()}`;
    const currency =
      window.Shopify?.currency?.active ||
      document.querySelector('meta[itemprop="priceCurrency"]')?.content ||
      'EUR';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((cents || 0) / 100);
    } catch {
      return `€${((cents || 0) / 100).toFixed(2)}`;
    }
  }

  toCents(priceString) {
    if (priceString == null) return null;
    const s = String(priceString).replace(',', '.');
    const n = Number.parseFloat(s);
    if (Number.isNaN(n)) return null;
    return Math.round(n * 100);
  }

  imgUrl(url, width) {
    if (!url) return '';
    if (typeof url === 'object') {
      url = url.src || url.url || '';
    }
    url = String(url || '');
    if (!url) return '';

    if (url.includes('cdn.shopify.com')) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    }
    return url;
  }

  plusIcon() {
    return `<svg viewBox="0 0 20 20" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 4v12M4 10h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    </svg>`;
  }

  escape(s) {
    return String(s || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  readCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.ts || !Array.isArray(parsed?.data)) return null;
      if (Date.now() - parsed.ts > this.cacheTtlMs) return null;
      return parsed.data;
    } catch {
      return null;
    }
  }

  writeCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {}
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawerCollectionRecommendations();
});
